import React, { useEffect, useState } from 'react';
import { FileText, Download, Eye, X, Copy, CircleCheck as CheckCircle2, Trash2, ChevronLeft, ChevronRight, ThumbsUp, ThumbsDown, TriangleAlert as AlertTriangle, CreditCard as Edit2, Save, MapPin, Phone, Mail, CreditCard, DollarSign, Calendar, User, TrendingUp, Building2, Hash } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import ContractAiReviewPanel from '../components/ContractAiReviewPanel';

interface Contract {
  id: string;
  client_name: string;
  cpf: string;
  phone: string;
  relative_phones: { numero: string; relacao: string }[];
  email: string;
  address: string;
  business_address: string;
  pix_key: string;
  loan_amount: number;
  installments: number;
  total_amount: number;
  status: string;
  approval_status: 'pending' | 'approved' | 'rejected';
  rejection_reason?: string;
  resubmitted_at?: string;
  approved_at: string | null;
  approved_by: string | null;
  created_at: string;
  ruta: number;
  observations?: string;
  seller: {
    display_name: string | null;
    email: string;
  } | null;
  contract_files: {
    id: string;
    file_name: string;
    file_url: string;
    file_type: string;
  }[];
  installments_data: {
    id: string;
    due_date: string;
    amount: number;
    status: string;
  }[];
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function ContractStatusBadge({ status }: { status: string }) {
  const s = status.toLowerCase();
  if (s === 'active') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200">
      <CheckCircle2 className="w-3 h-3" /> Ativo
    </span>
  );
  if (s === 'pending') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-200">
      <AlertTriangle className="w-3 h-3" /> Pendente
    </span>
  );
  if (s === 'completed') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 border border-blue-200">
      <CheckCircle2 className="w-3 h-3" /> Concluído
    </span>
  );
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200">
      {status}
    </span>
  );
}

function InstallmentStatusBadge({ status }: { status: string }) {
  const s = status.toLowerCase();
  if (s === 'paid') return (
    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">Pago</span>
  );
  if (s === 'overdue') return (
    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">Atrasado</span>
  );
  return (
    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">Pendente</span>
  );
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">{label}</p>
      <div className="text-sm font-semibold text-gray-900">{value}</div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function Contracts() {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [updatingApproval, setUpdatingApproval] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [contractToDelete, setContractToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [blacklistReason, setBlacklistReason] = useState('');
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [fileErrors, setFileErrors] = useState<Record<string, boolean>>({});
  const [editingObservations, setEditingObservations] = useState(false);
  const [observationsText, setObservationsText] = useState('');
  const [savingObservations, setSavingObservations] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    fetchContracts();
  }, []);

  const fetchContracts = async () => {
    try {
      let query = supabase
        .from('contracts')
        .select(`
          id,
          client_name,
          cpf,
          phone,
          relative_phones,
          email,
          address,
          business_address,
          pix_key,
          loan_amount,
          installments,
          total_amount,
          status,
          approval_status,
          rejection_reason,
          resubmitted_at,
          approved_at,
          approved_by,
          created_at,
          ruta,
          observations,
          seller_id,
          unit_id,
          payment_status,
          outstanding_balance,
          seller:seller_id(display_name, email),
          contract_files(id, file_name, file_url, file_type),
          installments_data:installments(id, due_date, amount, status)
        `)
        .eq('approval_status', 'pending')
        .eq('unit_id', user?.unit_id);

      if (user?.role === 'seller' && user?.ruta) {
        query = query.eq('ruta', user.ruta);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;

      setContracts(data || []);
    } catch (err) {
      if (err instanceof TypeError && err.message === 'Failed to fetch') {
        setError('Erro de conexão: Verifique sua conexão com a internet, configurações de VPN/firewall, ou se a URL e chave da API do Supabase estão corretas.');
      } else {
        setError('Erro ao carregar contratos');
      }
      console.error('Error fetching contracts:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveContractEvaluation = async (contract: Contract, evaluationType: 'like' | 'dislike', approvalStatus: 'approved' | 'rejected') => {
    try {
      const { error } = await supabase
        .from('contract_evaluations')
        .insert({
          contract_id: contract.id,
          unit_id: user?.unit_id,
          client_name: contract.client_name,
          cpf: contract.cpf,
          seller_id: contract.seller_id,
          ruta: contract.ruta,
          evaluation_type: evaluationType,
          approval_status: approvalStatus,
          loan_amount: contract.loan_amount,
          created_at: contract.created_at
        });

      if (error) throw error;
    } catch (err) {
      console.error('Error saving contract evaluation:', err);
    }
  };

  const handleApprovalChange = async (contractId: string, newStatus: 'approved' | 'rejected') => {
    if (!user) return;

    const contract = contracts.find(c => c.id === contractId);
    if (!contract) return;

    if (newStatus === 'rejected') {
      setSelectedContract(contract);
      setShowRejectionModal(true);
      return;
    }

    setUpdatingApproval(true);
    try {
      const { error } = await supabase
        .from('contracts')
        .update({
          approval_status: newStatus,
          approved_at: new Date().toISOString(),
          approved_by: user.id,
          outstanding_balance: contract.total_amount
        })
        .eq('id', contractId);

      if (error) throw error;

      if (newStatus === 'approved') {
        const { data: existingContracts, error: existingContractsError } = await supabase
          .from('contracts')
          .select('id')
          .eq('cpf', contract.cpf)
          .eq('approval_status', 'approved')
          .eq('unit_id', contract.unit_id)
          .neq('id', contract.id);

        if (existingContractsError) {
          console.error('Error checking existing contracts:', existingContractsError);
        }

        const isNewClient = !existingContracts || existingContracts.length === 0;
        const movementKind = isNewClient ? 'new_sale' : 'renewed_sale';

        const amountDisbursed = contract.loan_amount - 10;
        const { error: cashMovementError } = await supabase
          .from('cash_movements')
          .insert({
            kind: movementKind,
            amount: -Math.abs(amountDisbursed),
            description: `${isNewClient ? 'Nova venda' : 'Venda renovada'} - ${contract.client_name}`,
            contract_id: contract.id,
            user_id: contract.seller_id,
            unit_id: contract.unit_id,
            ruta: contract.ruta,
            created_at: new Date().toISOString()
          });

        if (cashMovementError) {
          console.error('Error recording cash movement:', cashMovementError);
        }
      }

      await saveContractEvaluation(contract, 'like', 'approved');

      // Update linked lead to approved (by direct link or CPF fallback)
      const { data: linkedLeads } = await supabase
        .from('leads')
        .select('id')
        .eq('contract_id', contractId)
        .limit(1);

      if (linkedLeads && linkedLeads.length > 0) {
        await supabase
          .from('leads')
          .update({ status: 'approved', converted_at: new Date().toISOString() })
          .eq('contract_id', contractId);
      } else {
        await supabase
          .from('leads')
          .update({ status: 'approved', contract_id: contractId, converted_at: new Date().toISOString() })
          .eq('cpf', contract.cpf)
          .eq('unit_id', contract.unit_id)
          .in('status', ['in_contact', 'awaiting_review', 'distributed']);
      }

      setContracts(contracts.filter(c => c.id !== contractId));

      if (selectedContract?.id === contractId) {
        setSelectedContract(null);
      }
    } catch (err) {
      console.error('Error updating contract approval:', err);
      setError('Erro ao atualizar status de aprovação');
    } finally {
      setUpdatingApproval(false);
    }
  };

  const handleRejectContract = async () => {
    if (!selectedContract || !user) return;

    setUpdatingApproval(true);
    try {
      const { error } = await supabase
        .from('contracts')
        .update({
          approval_status: 'rejected',
          approved_at: new Date().toISOString(),
          approved_by: user.id,
          rejection_reason: rejectionReason
        })
        .eq('id', selectedContract.id);

      if (error) throw error;

      await saveContractEvaluation(selectedContract, 'dislike', 'rejected');

      // Update linked lead to rejected by manager (by direct link or CPF fallback)
      const { data: linkedLeadsRej } = await supabase
        .from('leads')
        .select('id')
        .eq('contract_id', selectedContract.id)
        .limit(1);

      if (linkedLeadsRej && linkedLeadsRej.length > 0) {
        await supabase
          .from('leads')
          .update({ status: 'rejected_manager', rejection_reason: rejectionReason, rejected_by: user.id })
          .eq('contract_id', selectedContract.id);
      } else {
        await supabase
          .from('leads')
          .update({ status: 'rejected_manager', contract_id: selectedContract.id, rejection_reason: rejectionReason, rejected_by: user.id })
          .eq('cpf', selectedContract.cpf)
          .eq('unit_id', selectedContract.unit_id)
          .in('status', ['in_contact', 'awaiting_review', 'distributed']);
      }

      setContracts(contracts.filter(contract => contract.id !== selectedContract.id));
      setShowRejectionModal(false);
      setSelectedContract(null);
      setRejectionReason('');
    } catch (err) {
      console.error('Error rejecting contract:', err);
      setError('Erro ao rejeitar contrato');
    } finally {
      setUpdatingApproval(false);
    }
  };

  const handleDeleteContract = async () => {
    if (!contractToDelete || user?.role !== 'manager') return;
    if (!blacklistReason.trim()) return;

    const contract = contracts.find(c => c.id === contractToDelete);
    if (!contract) return;

    setDeleting(true);
    try {
      // Detach linked leads so they survive the contract deletion (history).
      // The remaining child tables have ON DELETE CASCADE at the database level.
      await supabase.from('leads').update({ contract_id: null }).eq('contract_id', contractToDelete);

      const { error } = await supabase.from('contracts').delete().eq('id', contractToDelete);

      if (error) throw error;

      // Add CPF to blacklist
      const cpfDigits = contract.cpf.replace(/\D/g, '');
      await supabase
        .from('cpf_blacklist')
        .upsert({
          cpf: cpfDigits,
          client_name: contract.client_name,
          reason: blacklistReason.trim(),
          deleted_from_unit_id: user?.unit_id || null,
          deleted_by_user_id: user?.id || null,
        }, { onConflict: 'cpf' });

      setContracts(contracts.filter(c => c.id !== contractToDelete));
      setContractToDelete(null);
      setShowDeleteConfirmation(false);
      setBlacklistReason('');

      if (selectedContract?.id === contractToDelete) {
        setSelectedContract(null);
      }
    } catch (err) {
      console.error('Error deleting contract:', err);
      const message = err instanceof Error ? err.message : 'Erro ao excluir contrato';
      setError(`Erro ao excluir contrato: ${message}`);
    } finally {
      setDeleting(false);
    }
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

  const formatCPF = (cpf: string) =>
    cpf.replace(/\D/g, '').replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');

  const formatPhone = (phone: string) => {
    const n = phone.replace(/\D/g, '');
    if (n.length === 11) return n.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    if (n.length === 10) return n.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    return phone;
  };

  const handleViewContract = (contract: Contract) => {
    setSelectedContract(contract);
    setCurrentFileIndex(0);
    setFileErrors({});
    setObservationsText(contract.observations || '');
    setEditingObservations(false);
  };

  const handleCopyClientData = (contract: Contract) => {
    const clientData = `
Dados do Cliente:
Nome: ${contract.client_name}
CPF: ${contract.cpf}
Telefone: ${contract.phone}

Telefones dos Parentes:
${contract.relative_phones.map(phone => `${phone.relacao}: ${phone.numero}`).join('\n')}

Email: ${contract.email}
Endereço Residencial: ${contract.address}
Endereço Comercial: ${contract.business_address}
Chave PIX: ${contract.pix_key}

Dados do Empréstimo:
Valor do Empréstimo: ${formatCurrency(contract.loan_amount)}
Valor Total: ${formatCurrency(contract.total_amount)}

Vendedor: ${contract.seller?.display_name || contract.seller?.email || 'N/A'}
Data de Criação: ${new Date(contract.created_at).toLocaleDateString('pt-BR')}
    `.trim();

    navigator.clipboard.writeText(clientData);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleNextFile = () => {
    if (!selectedContract) return;
    setCurrentFileIndex(prev =>
      prev < selectedContract.contract_files.length - 1 ? prev + 1 : prev
    );
  };

  const handlePreviousFile = () => {
    setCurrentFileIndex(prev => prev > 0 ? prev - 1 : prev);
  };

  const getCurrentFile = () => {
    if (!selectedContract || !selectedContract.contract_files.length) return null;
    return selectedContract.contract_files[currentFileIndex];
  };

  const isImage = (fileType: string, fileName?: string) =>
    fileType.startsWith('image/') ||
    /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(fileName ?? '');
  const isPDF = (fileType: string, fileName?: string) =>
    fileType === 'application/pdf' ||
    fileType === 'pdf' ||
    /\.pdf$/i.test(fileName ?? '');

  const handleFileDownload = (fileUrl: string, fileName: string) => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileError = (fileId: string) => {
    setFileErrors(prev => ({ ...prev, [fileId]: true }));
  };

  const handleSaveObservations = async () => {
    if (!selectedContract) return;

    setSavingObservations(true);
    try {
      const { error } = await supabase
        .from('contracts')
        .update({ observations: observationsText })
        .eq('id', selectedContract.id);

      if (error) throw error;

      setContracts(contracts.map(c =>
        c.id === selectedContract.id ? { ...c, observations: observationsText } : c
      ));
      setSelectedContract({ ...selectedContract, observations: observationsText });
      setEditingObservations(false);
    } catch (err) {
      console.error('Error saving observations:', err);
      setError('Erro ao salvar observações');
    } finally {
      setSavingObservations(false);
    }
  };

  // ── Loading / Error ──────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-3 bg-gray-50">
        <div className="w-10 h-10 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-gray-400">Carregando contratos...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-3 bg-gray-50 px-4">
        <div className="w-12 h-12 rounded-2xl bg-red-100 flex items-center justify-center">
          <AlertTriangle className="w-6 h-6 text-red-500" />
        </div>
        <p className="text-red-600 font-medium text-center max-w-sm">{error}</p>
      </div>
    );
  }

  const profit = selectedContract
    ? selectedContract.total_amount - selectedContract.loan_amount
    : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-5">

        {/* ── HEADER ── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900">Contratos Pendentes</h1>
              <p className="text-sm text-gray-400 mt-0.5">Analise e aprove contratos antes da liberacao</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-xl">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-semibold text-amber-700">
                  {contracts.length} aguardando analise
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ── CONTRACT CARDS ── */}
        {contracts.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm py-16 flex flex-col items-center gap-3 text-center px-6">
            <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-emerald-500" />
            </div>
            <p className="text-gray-700 font-semibold">Nenhum contrato pendente</p>
            <p className="text-sm text-gray-400">Todos os contratos foram analisados!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {contracts.map((contract) => {
              const gain = contract.total_amount - contract.loan_amount;
              const gainPct = contract.loan_amount > 0
                ? ((gain / contract.loan_amount) * 100).toFixed(0)
                : '0';

              return (
                <div
                  key={contract.id}
                  className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center gap-0">

                    {/* Left accent bar */}
                    <div className="hidden sm:block w-1 self-stretch bg-amber-400 flex-shrink-0 rounded-l-2xl" />

                    <div className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 flex-1 min-w-0">

                      {/* Col 1: Client */}
                      <div className="flex-1 min-w-0">
                        <p className="text-base font-bold text-gray-900 truncate">{contract.client_name}</p>
                        <p className="text-xs text-gray-400 font-mono mt-0.5">{formatCPF(contract.cpf)}</p>
                        <div className="flex flex-wrap items-center gap-2 mt-1.5">
                          <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                            <MapPin className="w-3 h-3" /> Ruta {contract.ruta}
                          </span>
                          <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                            <Calendar className="w-3 h-3" />
                            {new Date(contract.created_at).toLocaleDateString('pt-BR')}
                          </span>
                          <ContractStatusBadge status={contract.status} />
                        </div>
                      </div>

                      {/* Col 2: Financial */}
                      <div className="flex flex-row sm:flex-col items-center sm:items-end gap-4 sm:gap-0 flex-shrink-0">
                        <div className="text-center sm:text-right">
                          <p className="text-xs text-gray-400 uppercase tracking-wide">Emprestimo</p>
                          <p className="text-lg font-bold text-gray-900">{formatCurrency(contract.loan_amount)}</p>
                        </div>
                        <div className="text-center sm:text-right">
                          <p className="text-xs text-gray-400 uppercase tracking-wide">Total a pagar</p>
                          <p className="text-sm font-semibold text-blue-600">{formatCurrency(contract.total_amount)}</p>
                        </div>
                      </div>

                      {/* Col 3: Gain */}
                      <div className="hidden lg:block flex-shrink-0 text-right">
                        <p className="text-xs text-gray-400 uppercase tracking-wide">Lucro</p>
                        <p className="text-sm font-bold text-emerald-600">+{formatCurrency(gain)}</p>
                        <p className="text-xs text-emerald-500">{gainPct}%</p>
                      </div>

                      {/* Col 4: Installments */}
                      <div className="hidden lg:block flex-shrink-0 text-right">
                        <p className="text-xs text-gray-400 uppercase tracking-wide">Parcelas</p>
                        <p className="text-sm font-bold text-gray-700">{contract.installments}x</p>
                        <p className="text-xs text-gray-400">{formatCurrency(contract.total_amount / contract.installments)}</p>
                      </div>

                      {/* Col 5: Seller */}
                      <div className="hidden xl:block flex-shrink-0 text-right max-w-[140px]">
                        <p className="text-xs text-gray-400 uppercase tracking-wide">Vendedor</p>
                        <p className="text-xs font-medium text-gray-600 truncate">{contract.seller?.display_name || contract.seller?.email || 'N/A'}</p>
                      </div>

                      {/* Col 6: Actions */}
                      <div className="flex flex-row sm:flex-col items-center gap-2 flex-shrink-0">
                        {user?.role === 'manager' ? (
                          <>
                            <button
                              onClick={() => handleApprovalChange(contract.id, 'approved')}
                              disabled={updatingApproval}
                              title="Aprovar contrato"
                              className="flex items-center gap-1.5 h-9 px-4 bg-emerald-600 text-white text-xs font-semibold rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <ThumbsUp className="w-3.5 h-3.5" />
                              Aprovar
                            </button>
                            <button
                              onClick={() => handleApprovalChange(contract.id, 'rejected')}
                              disabled={updatingApproval}
                              title="Recusar contrato"
                              className="flex items-center gap-1.5 h-9 px-4 bg-red-600 text-white text-xs font-semibold rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <ThumbsDown className="w-3.5 h-3.5" />
                              Recusar
                            </button>
                          </>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-200">
                            <AlertTriangle className="w-3 h-3" /> Pendente
                          </span>
                        )}

                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleViewContract(contract)}
                            title="Ver detalhes"
                            className="h-9 w-9 flex items-center justify-center rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleCopyClientData(contract)}
                            title="Copiar dados"
                            className="h-9 w-9 flex items-center justify-center rounded-xl bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                          >
                            {copied ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                          </button>
                          {user?.role === 'manager' && (
                            <button
                              onClick={() => { setContractToDelete(contract.id); setShowDeleteConfirmation(true); }}
                              title="Excluir contrato"
                              className="h-9 w-9 flex items-center justify-center rounded-xl bg-gray-100 text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── CONTRACT DETAILS MODAL ── */}
      {selectedContract && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
          onClick={() => setSelectedContract(null)}
        >
          <div
            className="bg-white w-full sm:rounded-2xl shadow-2xl flex flex-col max-h-[100vh] sm:max-h-[95vh] sm:max-w-6xl overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="sticky top-0 z-10 bg-white border-b border-gray-100 px-4 sm:px-6 py-4 flex items-center gap-3 flex-shrink-0">
              <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center flex-shrink-0 shadow-sm">
                <span className="text-white text-sm font-bold">
                  {selectedContract.client_name.trim().split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-400 font-medium uppercase tracking-wide leading-none mb-0.5">Analise de Contrato</p>
                <h2 className="text-base font-bold text-gray-900 truncate">{selectedContract.client_name}</h2>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => handleCopyClientData(selectedContract)}
                  className={`h-9 flex items-center gap-1.5 px-3 text-sm font-medium rounded-xl transition-colors ${
                    copied ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  <span className="hidden sm:inline">{copied ? 'Copiado!' : 'Copiar'}</span>
                </button>
                <button
                  onClick={() => setSelectedContract(null)}
                  className="w-9 h-9 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                >
                  <X className="w-4 h-4 text-gray-600" />
                </button>
              </div>
            </div>

            {/* Modal body */}
            <div className="flex-1 overflow-hidden flex flex-col lg:flex-row min-h-0">

                {/* ── LEFT COLUMN ── */}
                <div className="flex-1 lg:w-1/2 lg:flex-none px-4 sm:px-6 py-5 space-y-5 overflow-y-auto border-b lg:border-b-0 lg:border-r border-gray-100">

                  {/* Financial Summary — decision card */}
                  <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-5 text-white">
                    <p className="text-xs font-semibold uppercase tracking-wide text-blue-200 mb-3">Resumo Financeiro</p>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-xs text-blue-200">Emprestimo</p>
                        <p className="text-lg font-bold">{formatCurrency(selectedContract.loan_amount)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-blue-200">Total a Pagar</p>
                        <p className="text-lg font-bold">{formatCurrency(selectedContract.total_amount)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-blue-200">Lucro</p>
                        <p className="text-lg font-bold text-emerald-300">
                          +{formatCurrency(selectedContract.total_amount - selectedContract.loan_amount)}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-blue-500 flex items-center justify-between">
                      <span className="text-xs text-blue-200">
                        {selectedContract.installments}x de {formatCurrency(selectedContract.total_amount / selectedContract.installments)}
                      </span>
                      <ContractStatusBadge status={selectedContract.status} />
                    </div>
                  </div>

                  {/* Client info */}
                  <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-50 bg-gray-50/60">
                      <User className="w-4 h-4 text-gray-400" />
                      <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Dados do Cliente</h3>
                    </div>
                    <div className="p-4 grid grid-cols-2 sm:grid-cols-3 gap-4">
                      <div className="col-span-2 sm:col-span-3">
                        <InfoRow label="Nome Completo" value={selectedContract.client_name} />
                      </div>
                      <InfoRow label="CPF" value={<span className="font-mono">{formatCPF(selectedContract.cpf)}</span>} />
                      <InfoRow label="Ruta" value={`Ruta ${selectedContract.ruta}`} />
                      <InfoRow
                        label="Telefone"
                        value={
                          <a
                            href={`https://wa.me/55${selectedContract.phone.replace(/\D/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-emerald-600 hover:text-emerald-700"
                          >
                            <Phone className="w-3.5 h-3.5" />
                            {formatPhone(selectedContract.phone)}
                          </a>
                        }
                      />
                      <div className="col-span-2 sm:col-span-2">
                        <InfoRow label="Email" value={selectedContract.email || '—'} />
                      </div>
                      <InfoRow label="Chave PIX" value={selectedContract.pix_key || '—'} />
                      <InfoRow label="Vendedor" value={selectedContract.seller?.display_name || selectedContract.seller?.email || 'N/A'} />
                      <InfoRow label="Data" value={new Date(selectedContract.created_at).toLocaleDateString('pt-BR')} />
                    </div>
                  </section>

                  {/* Relative phones */}
                  {selectedContract.relative_phones.filter(p => p.numero?.trim()).length > 0 && (
                    <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-50 bg-gray-50/60">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Contatos dos Parentes</h3>
                      </div>
                      <div className="p-4 space-y-2">
                        {selectedContract.relative_phones.filter(p => p.numero?.trim()).map((phone, index) => (
                          <div key={index} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
                            <span className="text-sm text-gray-600">{phone.relacao}</span>
                            <a
                              href={`https://wa.me/55${phone.numero.replace(/\D/g, '')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-600 hover:text-emerald-700"
                            >
                              <Phone className="w-3.5 h-3.5" />
                              {formatPhone(phone.numero)}
                            </a>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}

                  {/* Addresses */}
                  <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-50 bg-gray-50/60">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Enderecos</h3>
                    </div>
                    <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="bg-gray-50 rounded-xl p-3">
                        <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Residencial</p>
                        <p className="text-sm font-medium text-gray-800 break-words leading-relaxed">
                          {selectedContract.address || <span className="italic text-gray-400">Nao informado</span>}
                        </p>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-3">
                        <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Comercial</p>
                        <p className="text-sm font-medium text-gray-800 break-words leading-relaxed">
                          {selectedContract.business_address || <span className="italic text-gray-400">Nao informado</span>}
                        </p>
                      </div>
                    </div>
                  </section>

                  {/* Observations */}
                  <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50 bg-gray-50/60">
                      <div className="flex items-center gap-2">
                        <Edit2 className="w-4 h-4 text-gray-400" />
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Observacoes</h3>
                      </div>
                      {user?.role === 'manager' && !editingObservations && (
                        <button
                          onClick={() => setEditingObservations(true)}
                          className="h-7 flex items-center gap-1 px-2.5 text-xs font-medium text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                        >
                          <Edit2 className="w-3 h-3" /> Editar
                        </button>
                      )}
                    </div>
                    <div className="p-4">
                      {editingObservations ? (
                        <div className="space-y-3">
                          <textarea
                            value={observationsText}
                            onChange={(e) => setObservationsText(e.target.value)}
                            className="w-full h-28 px-3 py-2.5 text-sm text-gray-700 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 resize-none"
                            placeholder="Digite as observacoes sobre este contrato..."
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={handleSaveObservations}
                              disabled={savingObservations}
                              className="h-9 flex items-center gap-1.5 px-4 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50"
                            >
                              <Save className="w-3.5 h-3.5" />
                              {savingObservations ? 'Salvando...' : 'Salvar'}
                            </button>
                            <button
                              onClick={() => {
                                setEditingObservations(false);
                                setObservationsText(selectedContract.observations || '');
                              }}
                              disabled={savingObservations}
                              className="h-9 px-4 text-gray-600 bg-gray-100 text-sm font-semibold rounded-xl hover:bg-gray-200 transition-colors"
                            >
                              Cancelar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                          {selectedContract.observations || (
                            <span className="text-gray-400 italic">Nenhuma observacao registrada</span>
                          )}
                        </div>
                      )}
                    </div>
                  </section>

                  <ContractAiReviewPanel contractId={selectedContract.id} />

                  {/* Installments */}
                  <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50 bg-gray-50/60">
                      <div className="flex items-center gap-2">
                        <Hash className="w-4 h-4 text-gray-400" />
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Parcelas</h3>
                      </div>
                      <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                        {selectedContract.installments_data.length}x
                      </span>
                    </div>
                    <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {selectedContract.installments_data.map((installment, index) => (
                        <div
                          key={installment.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-100"
                        >
                          <div>
                            <p className="text-xs font-semibold text-gray-700">{index + 1}a Parcela</p>
                            <p className="text-xs text-gray-400 mt-0.5">
                              Venc. {new Date(installment.due_date + 'T00:00:00').toLocaleDateString('pt-BR')}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold text-gray-900">{formatCurrency(installment.amount)}</p>
                            <div className="mt-0.5">
                              <InstallmentStatusBadge status={installment.status} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                </div>

                {/* ── RIGHT COLUMN: DOCUMENTS ── */}
                <div className="flex-1 lg:w-1/2 lg:flex-none px-4 sm:px-6 py-5 flex flex-col gap-4 overflow-y-auto">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-gray-400" />
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Documentos</h3>
                    {selectedContract.contract_files.length > 0 && (
                      <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full ml-auto">
                        {selectedContract.contract_files.length} arquivo{selectedContract.contract_files.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>

                  {selectedContract.contract_files.length > 0 ? (
                    <>
                      {/* Navigation bar */}
                      <div className="flex items-center justify-between bg-gray-50 border border-gray-100 rounded-xl px-3 py-2">
                        <button
                          onClick={handlePreviousFile}
                          disabled={currentFileIndex === 0}
                          className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${
                            currentFileIndex === 0
                              ? 'text-gray-300 cursor-not-allowed'
                              : 'text-blue-600 hover:bg-blue-100'
                          }`}
                        >
                          <ChevronLeft className="w-5 h-5" />
                        </button>
                        <div className="text-center">
                          <p className="text-xs font-semibold text-gray-700">
                            {getCurrentFile()?.file_name}
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {currentFileIndex + 1} de {selectedContract.contract_files.length}
                          </p>
                        </div>
                        <button
                          onClick={handleNextFile}
                          disabled={currentFileIndex === selectedContract.contract_files.length - 1}
                          className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${
                            currentFileIndex === selectedContract.contract_files.length - 1
                              ? 'text-gray-300 cursor-not-allowed'
                              : 'text-blue-600 hover:bg-blue-100'
                          }`}
                        >
                          <ChevronRight className="w-5 h-5" />
                        </button>
                      </div>

                      {/* Document viewer */}
                      <div className="flex-1 border border-gray-100 rounded-2xl bg-gray-50 min-h-[320px] lg:min-h-[520px]">
                        {getCurrentFile() && (
                          <>
                            {!fileErrors[getCurrentFile()!.id] ? (
                              <>
                                {isImage(getCurrentFile()!.file_type, getCurrentFile()!.file_name) ? (
                                  <div className="w-full min-h-[320px] lg:min-h-[520px] flex items-center justify-center p-4">
                                    <img
                                      src={getCurrentFile()!.file_url}
                                      alt={getCurrentFile()!.file_name}
                                      className="max-w-full max-h-[500px] w-auto h-auto object-contain rounded-xl shadow-md"
                                      onError={() => handleFileError(getCurrentFile()!.id)}
                                    />
                                  </div>
                                ) : isPDF(getCurrentFile()!.file_type, getCurrentFile()!.file_name) ? (
                                  <div className="w-full" style={{ height: '520px' }}>
                                    <iframe
                                      src={getCurrentFile()!.file_url}
                                      className="w-full h-full rounded-2xl border-0"
                                      title={getCurrentFile()!.file_name}
                                    />
                                  </div>
                                ) : (
                                  <div className="w-full min-h-[320px] flex flex-col items-center justify-center text-center p-8 gap-3">
                                    <div className="w-16 h-16 rounded-2xl bg-gray-200 flex items-center justify-center mx-auto">
                                      <FileText className="w-8 h-8 text-gray-400" />
                                    </div>
                                    <p className="text-sm font-medium text-gray-600 break-words">{getCurrentFile()!.file_name}</p>
                                    <p className="text-xs text-gray-400">Tipo: {getCurrentFile()!.file_type}</p>
                                    <a
                                      href={getCurrentFile()!.file_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1.5 h-9 px-4 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors mt-1"
                                    >
                                      <Eye className="w-4 h-4" /> Abrir arquivo
                                    </a>
                                  </div>
                                )}
                              </>
                            ) : (
                              <div className="w-full h-full min-h-[320px] flex flex-col items-center justify-center text-center p-8">
                                <div className="w-14 h-14 rounded-2xl bg-red-100 flex items-center justify-center mx-auto mb-3">
                                  <AlertTriangle className="w-7 h-7 text-red-400" />
                                </div>
                                <p className="text-sm font-semibold text-red-600 mb-2">Erro ao carregar arquivo</p>
                                <p className="text-xs text-gray-500 break-words mb-4">{getCurrentFile()!.file_name}</p>
                                <div className="bg-red-50 border border-red-100 rounded-xl p-3 text-left mb-4 max-w-xs">
                                  <p className="text-xs font-semibold text-red-700 mb-1.5">Possiveis causas:</p>
                                  <ul className="text-xs text-red-600 space-y-1">
                                    <li>• Arquivo corrompido no upload</li>
                                    <li>• Formato nao suportado</li>
                                    <li>• Problema de conectividade</li>
                                  </ul>
                                </div>
                                <a
                                  href={getCurrentFile()!.file_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1.5 h-9 px-4 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors"
                                >
                                  <Eye className="w-4 h-4" /> Abrir em nova aba
                                </a>
                              </div>
                            )}
                          </>
                        )}
                      </div>

                      {/* Download buttons */}
                      {getCurrentFile() && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleFileDownload(getCurrentFile()!.file_url, getCurrentFile()!.file_name)}
                            className="flex-1 flex items-center justify-center gap-2 h-10 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors"
                          >
                            <Download className="w-4 h-4" /> Download
                          </button>
                          <a
                            href={getCurrentFile()!.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 flex items-center justify-center gap-2 h-10 bg-gray-100 text-gray-700 text-sm font-semibold rounded-xl hover:bg-gray-200 transition-colors"
                          >
                            <Eye className="w-4 h-4" /> Nova aba
                          </a>
                        </div>
                      )}

                      {/* Thumbnails */}
                      <div className="flex gap-2 overflow-x-auto pb-1">
                        {selectedContract.contract_files.map((file, index) => (
                          <button
                            key={file.id}
                            onClick={() => setCurrentFileIndex(index)}
                            className={`flex-shrink-0 relative p-2 border rounded-xl flex flex-col items-center gap-1.5 min-w-[80px] transition-colors ${
                              currentFileIndex === index
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                          >
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                              currentFileIndex === index ? 'bg-blue-100' : 'bg-gray-100'
                            }`}>
                              <FileText className={`w-4 h-4 ${currentFileIndex === index ? 'text-blue-600' : 'text-gray-400'}`} />
                            </div>
                            <span className="text-xs text-gray-500 truncate max-w-[64px] leading-tight">{file.file_name}</span>
                            {fileErrors[file.id] && (
                              <AlertTriangle className="w-3.5 h-3.5 text-red-500 absolute top-1.5 right-1.5" />
                            )}
                          </button>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="flex-1 flex flex-col items-center justify-center py-16 gap-3 bg-gray-50 rounded-2xl border border-gray-100">
                      <div className="w-14 h-14 rounded-2xl bg-gray-200 flex items-center justify-center">
                        <FileText className="w-7 h-7 text-gray-400" />
                      </div>
                      <p className="text-sm text-gray-500 font-medium">Nenhum documento disponivel</p>
                    </div>
                  )}
                </div>
            </div>

            {/* ── STICKY FOOTER: DECISION ACTIONS ── */}
            {user?.role === 'manager' && (
              <div className="flex-shrink-0 bg-white border-t border-gray-100 px-4 sm:px-6 py-4">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500 font-medium">
                      Analise os dados e documentos acima e tome uma decisao.
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5 truncate">
                      {selectedContract.client_name} &bull; {formatCurrency(selectedContract.loan_amount)} &bull; Ruta {selectedContract.ruta}
                    </p>
                  </div>
                  <div className="flex gap-3 flex-shrink-0">
                    <button
                      onClick={() => handleApprovalChange(selectedContract.id, 'rejected')}
                      disabled={updatingApproval}
                      className="flex-1 sm:flex-none flex items-center justify-center gap-2 h-11 px-6 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      <ThumbsDown className="w-4 h-4" />
                      Recusar Contrato
                    </button>
                    <button
                      onClick={() => handleApprovalChange(selectedContract.id, 'approved')}
                      disabled={updatingApproval}
                      className="flex-1 sm:flex-none flex items-center justify-center gap-2 h-11 px-6 bg-emerald-600 text-white font-semibold rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      {updatingApproval ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Aprovando...
                        </>
                      ) : (
                        <>
                          <ThumbsUp className="w-4 h-4" />
                          Aprovar Contrato
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── REJECTION REASON MODAL ── */}
      {showRejectionModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-base font-bold text-gray-900">Motivo da Recusa</h3>
              <button
                onClick={() => { setShowRejectionModal(false); setRejectionReason(''); }}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors text-gray-500"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div className="flex items-start gap-3 p-3.5 bg-red-50 border border-red-100 rounded-xl">
                <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-red-600 leading-relaxed">
                  Informe o motivo detalhado da recusa. Esta informacao sera registrada e o vendedor sera notificado.
                </p>
              </div>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="w-full h-32 px-3 py-2.5 text-sm text-gray-700 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-gray-50 resize-none"
                placeholder="Digite o motivo da recusa..."
                required
              />
              <div className="flex gap-2.5">
                <button
                  onClick={() => { setShowRejectionModal(false); setRejectionReason(''); }}
                  className="flex-1 h-11 border border-gray-200 text-gray-700 font-semibold text-sm rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleRejectContract}
                  disabled={!rejectionReason.trim() || updatingApproval}
                  className="flex-1 h-11 flex items-center justify-center gap-2 bg-red-600 text-white font-semibold text-sm rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {updatingApproval ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Recusando...
                    </>
                  ) : (
                    <>
                      <ThumbsDown className="w-4 h-4" />
                      Recusar Contrato
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE CONFIRMATION MODAL ── */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-base font-bold text-gray-900">Confirmar Exclusao</h3>
              <button
                onClick={() => { setShowDeleteConfirmation(false); setContractToDelete(null); setBlacklistReason(''); }}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors text-gray-500"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-5">
              <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-100 rounded-xl">
                <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-red-700">Esta acao nao pode ser desfeita</p>
                  <p className="text-xs text-red-600 mt-0.5 leading-relaxed">
                    Todos os dados do contrato, parcelas e arquivos serao permanentemente removidos. O CPF do cliente sera adicionado a BLACKLIST.
                  </p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Motivo da exclusao (obrigatorio) *
                </label>
                <input
                  type="text"
                  value={blacklistReason}
                  onChange={e => setBlacklistReason(e.target.value)}
                  placeholder="Ex: Calote, Fraude, Cliente problematico..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">Este motivo ficara registrado na blacklist do CPF.</p>
              </div>
              <div className="flex gap-2.5">
                <button
                  onClick={() => { setShowDeleteConfirmation(false); setContractToDelete(null); setBlacklistReason(''); }}
                  className="flex-1 h-11 border border-gray-200 text-gray-700 font-semibold text-sm rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeleteContract}
                  disabled={deleting || !blacklistReason.trim()}
                  className="flex-1 h-11 flex items-center justify-center gap-2 bg-red-600 text-white font-semibold text-sm rounded-xl hover:bg-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {deleting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Excluindo...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Excluir Contrato
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
